import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Loader2, Mail } from 'lucide-react'
import { FcGoogle } from 'react-icons/fc'
import { userService } from '@adthlete/shared-frontend'
import { useToast } from '@/components/ui/use-toast'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PasswordInput } from '@/components/ui/password-input'
import AvatarUpload from '@/components/common/AvatarUpload'
import PhoneVerification from '@/components/settings/PhoneVerification'
import { fetchCurrentUser, logoutThunk } from '@/redux/userSlice'
import type { AppDispatch, RootState } from '@/store'
import {
  useGetProviderLinkUrlMutation,
  useGetSignInMethodsQuery,
  useUnlinkProviderMutation,
} from '@/redux/marketerApi'
import { SettingsCard } from './SettingsCard'

const field =
  'h-11 w-full rounded-xl border border-border bg-card px-3 text-base text-foreground outline-none focus:border-[#84CC16] focus:ring-4 focus:ring-[#C0FD72]/40 disabled:bg-muted disabled:text-muted-foreground'
const outline =
  'inline-flex h-10 items-center gap-1.5 rounded-lg border border-border bg-card px-4 text-sm font-semibold text-foreground hover:bg-muted disabled:opacity-50'

const LINK_ERRORS: Record<string, string> = {
  already_linked: 'That account is already connected to another Athios user.',
  email_unverified: "That provider didn't confirm the email address.",
  cancelled: 'Connecting was cancelled.',
  expired: 'That took too long. Please try again.',
  not_configured: "That sign-in option isn't set up yet.",
}

/** Change password, in a dialog */
const PasswordDialog: React.FC<{ open: boolean; onOpenChange: (o: boolean) => void }> = ({
  open,
  onOpenChange,
}) => {
  const { toast } = useToast()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = async () => {
    if (!current || !next)
      return toast({ title: 'Fill in both passwords', variant: 'destructive' })
    if (next !== confirm)
      return toast({ title: "The new passwords don't match", variant: 'destructive' })
    if (next.length < 6)
      return toast({ title: 'Use at least 6 characters', variant: 'destructive' })
    setBusy(true)
    try {
      await userService.updatePassword({ currentPassword: current, newPassword: next })
      toast({ title: 'Password changed' })
      setCurrent('')
      setNext('')
      setConfirm('')
      onOpenChange(false)
    } catch (err: any) {
      toast({
        title: "Couldn't change the password",
        description: err?.response?.data?.error ?? 'Check your current password.',
        variant: 'destructive',
      })
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border bg-card sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>Use at least 6 characters.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <PasswordInput
            placeholder="Current password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
          <PasswordInput
            placeholder="New password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          <PasswordInput
            placeholder="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="mt-2 inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#C0FD72] font-semibold text-[#0F172A]"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Change password
        </button>
      </DialogContent>
    </Dialog>
  )
}

/** Settings > Account: who you are, how you sign in, and closing the account */
export const AccountTab: React.FC = () => {
  const { toast } = useToast()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const user = useSelector((state: RootState) => state.user.currentUser) as any
  const [params, setParams] = useSearchParams()
  const [name, setName] = useState(user?.name ?? '')
  const [savingName, setSavingName] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [closing, setClosing] = useState(false)
  const { data: methods } = useGetSignInMethodsQuery()
  const [getLink, { isLoading: linking }] = useGetProviderLinkUrlMutation()
  const [unlink, { isLoading: unlinking }] = useUnlinkProviderMutation()
  const handled = useRef(false)

  useEffect(() => setName(user?.name ?? ''), [user?.name])

  // Back from connecting Google
  useEffect(() => {
    if (handled.current) return
    const linked = params.get('linked')
    const error = params.get('oauth_error')
    if (!linked && !error) return
    handled.current = true
    if (linked)
      toast({
        title: `${linked === 'google' ? 'Google' : 'Apple'} connected`,
        description: 'You can now sign in with it.',
      })
    if (error)
      toast({
        title: "Couldn't connect",
        description: LINK_ERRORS[error] ?? 'Please try again.',
        variant: 'destructive',
      })
    setParams({ tab: 'account' }, { replace: true })
  }, [params, setParams, toast])

  const saveName = async () => {
    setSavingName(true)
    try {
      await userService.updateProfile({ name: name.trim() })
      await dispatch(fetchCurrentUser())
      toast({ title: 'Name updated' })
    } catch {
      toast({ title: "Couldn't save your name", variant: 'destructive' })
    } finally {
      setSavingName(false)
    }
  }

  const connect = async (provider: 'google' | 'apple') => {
    try {
      const { url } = await getLink(provider).unwrap()
      window.location.assign(url)
    } catch (err: any) {
      toast({
        title: err?.status === 503 ? 'Coming soon' : "Couldn't connect",
        description: err?.data?.error ?? 'Try again.',
      })
    }
  }

  const closeAccount = async () => {
    setClosing(true)
    try {
      await userService.closeAccount()
      await dispatch(logoutThunk())
      toast({ title: 'Account closed' })
      navigate('/signin')
    } catch (err: any) {
      toast({
        title: "Couldn't close the account",
        description: err?.response?.data?.error,
        variant: 'destructive',
      })
      setClosing(false)
    }
  }

  const providerRow = (provider: 'google' | 'apple') => {
    const linked = methods?.providers[provider]
    const label = provider === 'google' ? 'Google' : 'Apple'
    return (
      <li key={provider} className="flex flex-wrap items-center gap-4 py-4">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-border">
          <FcGoogle className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-foreground">{label}</p>
          <p className="truncate text-sm text-muted-foreground">
            {linked ? (linked.email ?? 'Connected') : 'Not connected'}
          </p>
        </div>
        {linked ? <span className="text-sm font-semibold text-[#16A34A]">Connected</span> : null}
        {linked ? (
          <button
            type="button"
            className={outline}
            disabled={unlinking}
            onClick={() => unlink(provider)}
          >
            Disconnect
          </button>
        ) : (
          <button
            type="button"
            className={outline}
            disabled={linking}
            onClick={() => connect(provider)}
          >
            Connect
            {methods && !methods.available[provider] ? (
              <span className="rounded-full bg-muted px-1.5 text-xs text-muted-foreground">
                Soon
              </span>
            ) : null}
          </button>
        )}
      </li>
    )
  }

  return (
    <div className="max-w-3xl space-y-4">
      <SettingsCard title="Profile information">
        <div className="flex flex-wrap items-center gap-4">
          <AvatarUpload
            currentAvatar={user?.avatar}
            userName={user?.name || user?.email}
            size="lg"
          />
          <p className="text-sm text-muted-foreground">
            Tap the photo to change it.
            <br />
            Recommended size: 400×400px.
          </p>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-foreground">Full name</span>
            <div className="flex gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                className={field}
              />
              {name.trim() && name !== (user?.name ?? '') ? (
                <button
                  type="button"
                  onClick={saveName}
                  disabled={savingName}
                  className="h-11 shrink-0 rounded-xl bg-[#C0FD72] px-4 font-semibold text-[#0F172A]"
                >
                  {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </button>
              ) : null}
            </div>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-foreground">Email address</span>
            <input value={user?.email ?? ''} disabled className={field} />
          </label>
        </div>
        <div className="mt-4">
          <PhoneVerification embedded />
        </div>
      </SettingsCard>

      <SettingsCard
        title="Sign in methods"
        subtitle="Manage how you sign in to your Athios account."
      >
        <ul className="divide-y divide-border">
          <li className="flex flex-wrap items-center gap-4 pb-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-border">
              <Mail className="h-5 w-5 text-foreground" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-foreground">Email and password</p>
              <p className="truncate text-sm text-muted-foreground">
                {methods?.email ?? user?.email}
              </p>
            </div>
            <span className="rounded-md bg-[#E8F9D2] px-2 py-0.5 text-xs font-semibold text-[#2F5E06]">
              Primary
            </span>
            {!methods || methods.hasPassword ? (
              <button type="button" className={outline} onClick={() => setPasswordOpen(true)}>
                Change password
              </button>
            ) : null}
          </li>
          {/* Google only for now; Apple sign-in isn't offered */}
          {providerRow('google')}
        </ul>
        <p className="text-xs text-muted-foreground">
          You can always sign in with a link emailed to you.
        </p>
      </SettingsCard>

      <section className="rounded-2xl border border-[#FECACA] bg-[#FEF2F2] p-5">
        <h2 className="font-bold text-foreground">Danger zone</h2>
        <p className="text-sm text-muted-foreground">
          Permanently close your account. You're signed out and removed from the platform; data we
          must keep for legal or tax reasons is retained and de-identified later.
        </p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              disabled={closing}
              className="mt-3 h-10 rounded-lg bg-[#DC2626] px-4 text-sm font-semibold text-[#FFFFFF] hover:bg-[#B91C1C]"
            >
              Close my account
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Close your account?</AlertDialogTitle>
              <AlertDialogDescription>
                You'll be signed out right away and won't be able to sign back in.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep my account</AlertDialogCancel>
              <AlertDialogAction
                onClick={closeAccount}
                className="bg-[#DC2626] text-[#FFFFFF] hover:bg-[#B91C1C]"
              >
                Close account
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </section>

      <PasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
    </div>
  )
}

export default AccountTab
